import { FC, useEffect } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import { RuleID } from '@forto-dev/insights-engine-models'
import { ChannelType, CorrelationType } from '@forto-dev/notification-center-models'
import { Card, Cluster, Stack, Typography } from '@forto-dev/phoenix-ui-core'
import { Separator } from '@forto-dev/phoenix-ui-core/common'

import { InsightBlock } from '@/features/feed'
import { useLocalisation } from '@/utils/localisation'

import { notificationPreviewQueryOptions } from '../api/notifications.service'
import { useUpdateNotificationStatus } from '../api/useUpdateNotificationStatus'
import { useNotificationCenterSearch, useNotifications } from '../hooks'
import { useIsSurveyEnabled } from '../hooks/survey/useIsSurveyEnabled'
import { MOTUsefulnessSurvey } from '../survey/MOTUsefulnessSurvey'

export type NotificationDetailsProps = {
  id: string
}

export const NotificationDetails: FC<NotificationDetailsProps> = ({ id }) => {
  const { currentLanguage, translateDate } = useLocalisation()
  const language = currentLanguage.substring(0, 2)
  const updateStatusMutation = useUpdateNotificationStatus()
  const { data: preview } = useSuspenseQuery(notificationPreviewQueryOptions(id, language))
  const { search } = useNotificationCenterSearch()
  const { items: notifications } = useNotifications({
    type: search.type,
    status: search.status,
    startDate: search.durationFrom,
    endDate: search.durationTo,
  })
  const notificationFromList = notifications.find((notification) => notification.id === id)
  const notificationReference = preview?.reference as RuleID
  const isSurveyEnabled = useIsSurveyEnabled(notificationReference)

  useEffect(() => {
    updateStatusMutation.mutate({
      data: {
        ids: [id],
        status: 'read',
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  return (
    <Card>
      <Cluster gap={8}>
        <Cluster />
        <Stack fullWidth>
          {notificationFromList ? (
            <Stack gap={16}>
              <Typography variant="tiny">
                {translateDate('notifications_date', notificationFromList.createdAt)}
              </Typography>
              <Separator orientation="horizontal" />
            </Stack>
          ) : null}
          <InsightBlock html={preview.renderedContent} />
          {isSurveyEnabled && notificationReference && (
            <Stack gap={16}>
              <MOTUsefulnessSurvey
                reference={notificationReference}
                channelType={ChannelType.Web}
                correlationID={id}
                correlationType={CorrelationType.UserNotification}
              />
            </Stack>
          )}
        </Stack>
      </Cluster>
    </Card>
  )
}
